import { initializeDB } from '../config/db';
import { EMPLOYEE_STORE, URL } from '../config/constants';

const employeeServiceExtended = {
    // Sync employees from server
    async syncEmployees() {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${URL}/employees`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const employees = await response.json();
            
            // Save to IndexedDB
            const db = await initializeDB();
            const tx = db.transaction(EMPLOYEE_STORE, 'readwrite');
            const store = tx.objectStore(EMPLOYEE_STORE);
            
            // Clear existing data
            await store.clear();
            
            // Add new data
            for (const employee of employees) {
                await store.put({
                    id: employee.id,
                    fullName: employee.fullName || employee.FullName || '',
                    division: employee.division || employee.Division || '',
                    phone: employee.phone || employee.Phone || '',
                    jobClassification: employee.jobClassification || employee.JobClassification || '',
                    companyCode: employee.companyCode || employee.CompanyCode || '',
                    employeeId: employee.employeeId || employee.EmployeeId || '',
                    email: employee.email || '',
                    mvr: employee.mvr || employee.MVR || '',
                    status: employee.status !== undefined ? employee.status : true,
                    updatedAt: new Date().toISOString()
                });
            }
            
            await tx.done;
            
            // Update sync timestamp
            localStorage.setItem('lastEmployeeSyncTime', new Date().toISOString());
            
            return employees;
        } catch (error) {
            console.error('Error syncing employees:', error);
            throw error;
        }
    },

    // Get all employees from IndexedDB
    async getEmployees() {
        try {
            const db = await initializeDB();
            const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
            const store = tx.objectStore(EMPLOYEE_STORE);
            const employees = await store.getAll();
            await tx.done;
            
            return employees.filter(emp => emp.status !== false);
        } catch (error) {
            console.error('Error getting employees:', error);
            return [];
        }
    },

    // Get employee by ID
    async getEmployeeById(id) {
        try {
            const db = await initializeDB();
            const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
            const store = tx.objectStore(EMPLOYEE_STORE);
            const employee = await store.get(id);
            await tx.done;
            
            return employee || null;
        } catch (error) {
            console.error('Error getting employee by ID:', error);
            return null;
        }
    },

    // Search employees by FullName or Phone
    async searchEmployees(searchTerm) {
        try {
            const employees = await this.getEmployees();
            
            if (!searchTerm) {
                return employees;
            }
            
            const search = searchTerm.toLowerCase();
            
            return employees.filter(emp => 
                emp.fullName?.toLowerCase().includes(search) ||
                emp.phone?.toLowerCase().includes(search)
            );
        } catch (error) {
            console.error('Error searching employees:', error);
            return [];
        }
    },

    /**
     * Get full employee data by names
     * @param {Array} employeeBasicList - List of employees with at least FullName field
     * @returns {Promise<Array>} Full employee data
     */
    async getFullEmployeesByNames(employeeBasicList) {
        try {
            const db = await initializeDB();
            const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
            const store = tx.objectStore(EMPLOYEE_STORE);
            const allEmployees = await store.getAll();
            
            return employeeBasicList.map(emp => {
                // Find full employee data by name
                const fullEmployee = allEmployees.find(e => 
                    e.FullName === emp.FullName || 
                    e.fullName === emp.FullName ||
                    e.FullName === emp.fullName ||
                    e.fullName === emp.fullName
                );
                
                if (fullEmployee) {
                    return {
                        ...fullEmployee,
                        impact: emp.impact || ''
                    };
                }
                
                // Fallback if not found
                return emp;
            });
        } catch (error) {
            console.error('Error getting full employee data:', error);
            return employeeBasicList;
        }
    }
};

export default employeeServiceExtended;